import type { TableProps } from 'antd';
import type { FilterValue, SorterResult } from 'antd/es/table/interface';
import { get } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import type { ITableSortState } from '../utils/buildTableColumns';

export type ITableFilterState = Record<string, FilterValue | null>;

const compareValues = (a: unknown, b: unknown) => {
  if (a == null) return b == null ? 0 : 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

/**
 * Sort and filter state for tables that hold their rows in memory. antd only
 * renders the controls; rows are sorted here and filtered by the caller, so
 * they stay the source objects that identity-based row lookups expect.
 */
export const useLocalTableView = <T extends object>(data: T[]) => {
  const [sort, setSort] = useState<ITableSortState>({});
  const [filters, setFilters] = useState<ITableFilterState>({});

  const handleTableChange: TableProps<T>['onChange'] = useCallback(
    (
      _pagination: unknown,
      nextFilters: ITableFilterState,
      sorter: SorterResult<T> | SorterResult<T>[]
    ) => {
      const active = Array.isArray(sorter) ? sorter[0] : sorter;
      const column = active?.column as
        | { sortName?: string; key?: string }
        | undefined;
      setSort(
        active?.order && column
          ? { sortName: column.sortName || column.key, sortOrder: active.order }
          : {}
      );
      setFilters(nextFilters);
    },
    []
  );

  const sortedData = useMemo(() => {
    const { sortName, sortOrder } = sort;
    if (!sortName || !sortOrder) return data;
    const direction = sortOrder === 'ascend' ? 1 : -1;
    return [...data].sort(
      (a, b) => direction * compareValues(get(a, sortName), get(b, sortName))
    );
  }, [data, sort]);

  return { sort, filters, sortedData, handleTableChange };
};
